import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";

// natural lot order: 2 before 10
const byLotNumber = (a, b) =>
  String(a.lotNumber ?? "").localeCompare(String(b.lotNumber ?? ""), undefined, {
    numeric: true,
  });

// Approve a request: the lots the bidder can bid on, ticked from his
// interest (all lots when he chose all). None ticked: he becomes a
// participant now and lots are allotted later.
const ApproveDialog = ({ request, lots, onClose, onApprove, saving }) => {
  const [chosen, setChosen] = useState([]);
  const sorted = [...(lots || [])].sort(byLotNumber);
  const interested = request?.interestedEventItemIds;

  useEffect(() => {
    if (!request) return;
    const lotIds = sorted.map((lot) => lot.eventItemId);
    setChosen(interested ? lotIds.filter((id) => interested.includes(id)) : lotIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const name = request
    ? [request.user?.firstName, request.user?.lastName].filter(Boolean).join(" ")
    : "";
  const toggle = (id) =>
    setChosen((current) =>
      current.includes(id) ? current.filter((lotId) => lotId !== id) : [...current, id]
    );

  return (
    <Dialog open={Boolean(request)} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Approve {name}
        {request?.company?.companyName ? ` (${request.company.companyName})` : ""}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: "#4B5563", mb: 1 }}>
          {interested
            ? `Interested in lots ${request.interestedLotNumbers?.join(", ")}, so they are ticked to start. Add or remove any lots.`
            : "Interested in all lots, so all are ticked to start. Untick the ones he shouldn't bid on."}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mb: 1, alignItems: "center" }}>
          <Button size="small" onClick={() => setChosen(sorted.map((lot) => lot.eventItemId))}>
            Select all
          </Button>
          <Button size="small" onClick={() => setChosen([])}>
            Select none
          </Button>
          <Typography variant="body2" sx={{ ml: "auto", color: "#4B5563" }}>
            {chosen.length} of {sorted.length} selected
          </Typography>
        </Box>
        <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
          {sorted.map((lot) => (
            <Box
              key={lot.eventItemId}
              sx={{ display: "flex", alignItems: "center", borderBottom: "1px solid #EEF0F3" }}
            >
              <FormControlLabel
                sx={{ flex: 1, mr: 0 }}
                control={
                  <Checkbox
                    checked={chosen.includes(lot.eventItemId)}
                    onChange={() => toggle(lot.eventItemId)}
                  />
                }
                label={`Lot ${lot.lotNumber} · ${lot.shortDescription || ""}`}
              />
              {interested?.includes(lot.eventItemId) && (
                <Box
                  component="span"
                  sx={{ backgroundColor: "#E8EDFD", color: "#1C3BB0", fontSize: 12, px: 1, py: 0.25 }}
                >
                  Interested
                </Box>
              )}
            </Box>
          ))}
        </Box>
        {chosen.length === 0 && (
          <Box sx={{ mt: 2, p: 1.5, backgroundColor: "#FFF4CC", border: "1px solid #E6B800", fontSize: 14 }}>
            No lots ticked: {name || "the bidder"} becomes a participant but can't
            bid until you allot lots in View Lots &amp; Buyer.
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onApprove(chosen)}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ backgroundColor: "#1E7B4A", borderRadius: 0, "&:hover": { backgroundColor: "#166B3F" } }}
        >
          {chosen.length === 0
            ? "Approve, allot lots later"
            : `Approve and allot ${chosen.length} ${chosen.length === 1 ? "lot" : "lots"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApproveDialog;
